'use client'

import { Box, Flex, Stack, Text } from '@mantine/core'
import { type DataTableSortStatus } from 'mantine-datatable'
import { Suspense } from 'react'
import { useShallow } from 'zustand/react/shallow'

import CoinIcon from '@/components/CoinIcon'
import { SectionErrorBoundary } from '@/components/error/boundary'
import useSubscribeLiveCoinsMarket from '@/hooks/business/useSubscribeLiveCoinsMarket'
import { useIsResponsiveMobileView } from '@/hooks/responsive'
import { I18nLink } from '@/i18n/navigation'

import type { KnownSearchParams } from '../../../page'
import { CRYPTO_FETCH_BLOCK_SIZE, CRYPTO_PAGE_SIZE } from '../../CoinsTable/configs'
import { CoinsTableProvider, useCoinsTableStore } from '../../CoinsTable/context'
import { TableSkeleton } from '../../CoinsTable/Skeleton'
import type { TableRecord } from '../../CoinsTable/Table/columns/configs'
import Price from '../../CoinsTable/Table/columns/Price'
import PriceChange from '../../CoinsTable/Table/columns/PriceChange'
import PriceChart from '../../CoinsTable/Table/columns/PriceChart'
import EmptyState from '../../CoinsTable/Table/EmptyState'
import { useCoinRestrictedAreas } from '../../CoinsTable/Table/hooks/useCoinRestrictedAreas'
import useTableData from '../../CoinsTable/Table/hooks/useTableData'
import CursorPagination from '../StockTable/CursorPagination'
import styles from '../style.module.css'
import DesktopCoinsTable from './Table'

interface CoinsTableProps {
  ssrSearchParams: KnownSearchParams
  initialSortStatus: DataTableSortStatus<TableRecord>
}

export default function CoinsTable({ ssrSearchParams, initialSortStatus }: CoinsTableProps) {
  const isMobileView = useIsResponsiveMobileView()

  return (
    <Suspense fallback={<TableSkeleton />}>
      <CoinsTableProvider ssrSearchParams={ssrSearchParams}>
        <SectionErrorBoundary>
          {isMobileView ? (
            <MobileCoinsTable initialSortStatus={initialSortStatus} />
          ) : (
            <DesktopCoinsTable initialSortStatus={initialSortStatus} />
          )}
        </SectionErrorBoundary>
      </CoinsTableProvider>
    </Suspense>
  )
}

function MobileCoinsTable({
  initialSortStatus,
}: {
  initialSortStatus: DataTableSortStatus<TableRecord>
}) {
  const { isPending, records, totalRecords } = useTableData({
    initialSortStatus,
    pageSize: CRYPTO_PAGE_SIZE,
    sortInRequest: true,
    fetchBlockSize: CRYPTO_FETCH_BLOCK_SIZE,
  })

  const { currentPage, updateFilters, updateLiveCoinsMarket, clearLiveCoinsMarket } =
    useCoinsTableStore(
      useShallow(state => ({
        currentPage: state.currentPage,
        updateFilters: state.updateFilters,
        updateLiveCoinsMarket: state.updateLiveCoinsMarket,
        clearLiveCoinsMarket: state.clearLiveCoinsMarket,
      })),
    )

  useSubscribeLiveCoinsMarket(
    records.map(coin => coin.slug),
    {
      updateLiveCoinsMarket,
      clearLiveCoinsMarket,
    },
  )

  const { visibleCoins } = useCoinRestrictedAreas(records)

  if (visibleCoins.length === 0) {
    return <EmptyState />
  }

  return (
    <Stack gap="16px">
      <Stack gap={0}>
        {visibleCoins.map(coin => (
          <CoinRow key={coin.token_id} coin={coin} />
        ))}
      </Stack>
      <CursorPagination
        currentPage={currentPage}
        loadedPageCount={Math.ceil(totalRecords / CRYPTO_PAGE_SIZE)}
        totalRecords={totalRecords}
        isFetching={isPending}
        recordsPerPage={CRYPTO_PAGE_SIZE}
        onChange={page => {
          updateFilters({ currentPage: page })
        }}
      />
    </Stack>
  )
}

function CoinRow({ coin }: { coin: TableRecord }) {
  return (
    <I18nLink prefetch={false} href={`/${coin.slug}`}>
      <Flex className={styles.mobileCoinRow} align="center" wrap="nowrap">
        <Flex gap="8px" align="center" flex={1} miw={0}>
          <CoinIcon coin={coin} size={28} />
          <Box flex={1} miw={0}>
            <Text variant="body" c="var(--content-primary)" truncate title={coin.name}>
              {coin.name}
            </Text>
            <Text variant="body" c="var(--content-tertiary)" truncate title={coin.symbol}>
              {coin.symbol}
            </Text>
          </Box>
        </Flex>

        <Box w={80} ms="12px" style={{ flexShrink: 0 }}>
          <PriceChart coinId={coin.token_id} />
        </Box>

        <Stack gap="2px" align="flex-end" style={{ minWidth: 100 }} flex={1}>
          <Price coin={coin} />
          <PriceChange coin={coin} />
        </Stack>
      </Flex>
    </I18nLink>
  )
}
